import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { getIntegrationAccess } from '../../../api/integrationAccess.api';
import { queryKeys } from '../../../api/queryKeys';
import { reportError } from '../../../helpers/reportError';
import { useAuth } from '../../../store/auth';
import {
  IntegrationAccess,
  IntegrationAccessScope,
} from '../../../types/api/integrationAccess.types';

/**
 * The POS + marketplace access a partner recorded for one location. One query per scope;
 * every mutation in useIntegrationAccessActions writes the server's answer back into it.
 *
 * Onboarding responses (/v1/auth/me and every onboarding route) already carry the object,
 * so for the incomplete scope it seeds the cache and the POS step opens on the right scene
 * without a round trip. Still refetched right away: the seed only covers the first paint.
 */
export const useIntegrationAccess = (
  scope: IntegrationAccessScope | null,
  { enabled = true }: { enabled?: boolean } = {}
) => {
  const incomplete = useAuth((s) => s.incompleteLocation);
  const seed =
    scope &&
    'incompleteLocationId' in scope &&
    incomplete &&
    String(incomplete.id) === String(scope.incompleteLocationId)
      ? incomplete.integrationAccess ?? undefined
      : undefined;

  const query = useQuery({
    queryKey: queryKeys.integrationAccess.detail(
      scope ?? { locationId: 'none' }
    ),
    queryFn: async (): Promise<IntegrationAccess> => {
      const { data } = await getIntegrationAccess(
        scope as IntegrationAccessScope
      );
      return data.data;
    },
    enabled: enabled && scope != null,
    initialData: seed,
    staleTime: 0,
  });

  // useQuery has no onError; a failed load is reported once, like the writes are.
  useEffect(() => {
    if (query.error) reportError(query.error);
  }, [query.error]);

  return {
    data: query.data ?? null,
    /** True until the first answer lands; false while a refetch refreshes what is shown. */
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
};
